import React, { FC, useEffect, ReactElement, useContext, useRef } from 'react';
import { getCompetitions } from '../../services/http/httpService';
import { CompetitionsContext, PrefferencesContext } from '../../context/context';
import Dropdown from '../dropdown/dropdown';
import Table from '../table/table';
import Matches from '../matches/matches';
import {
  getLanguageOption,
  getTenantFromHost,
  getDefaultCompetitionIdQuery
} from '../../helpers/helperFunctions';

const MainPage: FC = (): ReactElement => {
  const { setCompetitions, setSelectedCompetition, setMatchDay } = useContext(CompetitionsContext);
  const {
    theme,
    setTheme,
    language,
    setLanguage,
    parentHost,
    setParentHost,
    setWidgetWidth
  } = useContext(PrefferencesContext);

  const mainPageRef = useRef<HTMLDivElement>(null);

  // Determine widget width
  useEffect(() => {
    const widthListener = () => {
      if (mainPageRef.current) {
        const width = mainPageRef.current.scrollWidth;
        setWidgetWidth(width);
      }
    };
    widthListener();
    window.addEventListener('resize', widthListener);
    return () => window.removeEventListener('resize', widthListener);
  }, []);

  // Determine host of parent window (to set up appropriate fonts)
  useEffect(() => {
    try {
      setParentHost(getTenantFromHost());
    } catch (e) {
      console.log('Error processing parent host');
    }
  }, []);

  // Determine language queryString option
  useEffect(() => {
    try {
      setLanguage(getLanguageOption());
    } catch (e) {
      console.log('Error processing language option');
    }
  }, []);

  window.addEventListener('message', (event) => {
    // this check is needed because of https://developer.mozilla.org/en-US/docs/Web/API/Window/postMessage#security_concerns
    if (event.data.type === 'colorMode') {
      // assuming you have a function setColorModeInChildApp in your app
      setTheme(event.data.mode); // will be the string 'dark' or 'light'
    }
  });

  // Fetches competitions when widget is loaded, and sets a selected competition, and a default competition if there is one
  useEffect(() => {
    if (!language) return;
    getCompetitions(language)
      .then((response) => {
        setCompetitions(response);
        let competitionIndex = 0;
        const defaultCompetition = getDefaultCompetitionIdQuery();
        if (defaultCompetition) {
          competitionIndex = response.findIndex(
            (element) => element.competitionId === defaultCompetition
          );
        }
        setSelectedCompetition(response[competitionIndex]);
        setMatchDay(Number(response[competitionIndex].currentMatchday));
      })
      .catch((e) => console.log('Error fetching competitions'));
  }, [language]);

  return (
    <div className={`_${parentHost} main-page main-page-${theme}`} ref={mainPageRef}>
      <Dropdown />
      <div className="table-results-wrapper">
        <div className="flex1" />
        <div className="table">
          <Matches />
          <Table />
        </div>
        <div className="flex1" />
      </div>
    </div>
  );
};

export default MainPage;
